import { Component, OnInit } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { Workbook } from 'exceljs';
import * as fs from 'file-saver';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { GlobalVariable } from 'src/app/global';
import { EmpleadosService } from '../../services/empleados/empleados.service';
import { EmpresaService } from 'src/app/base/services/empresas/empresa.service';
import { MapaRecorridoCampoComponent } from '../mapa-recorrido-campo/mapa-recorrido-campo.component';

type Pestana = 'equipo' | 'detalle' | 'mensual' | 'colegios';

/**
 * Reportes del control de personal de campo (requerimiento de Editorial Contexto, sección 6):
 *  A. Detalle diario por asesor: cada lugar con llegada, salida, tiempo, traslado, hueco,
 *     distancia y foto, y el resultado del turno y del día.
 *  B. Resumen diario del equipo: una fila por asesor y día, con horas, metas y alertas.
 *  C. Resumen mensual: una fila por asesor con los totales del período (la hoja "Resumen
 *     Incentivo" del Excel, todavía sin las columnas del incentivo).
 *  D. Mapa del recorrido de un asesor en un día (botón "Ver mapa" del detalle).
 *  E. Visitas por colegio: quién fue, cuántas veces, la última visita y los colegios sin visitas.
 *
 * El cálculo lo hace el servidor (trunk/app/funciones/RRHH/rrhh-control-campo-calculo.js);
 * acá sólo se muestra y se exporta.
 */
@Component({
  selector: 'app-reporte-control-campo',
  templateUrl: './reporte-control-campo.component.html',
  styleUrls: ['./reporte-control-campo.component.css'],
  standalone: false
})
export class ReporteControlCampoComponent implements OnInit {

  /** Lo asigna quien abre el modal. */
  id_empresa: number;
  razon_social = '';

  rest_server = GlobalVariable.SERVER_URL;
  pestana: Pestana = 'equipo';
  desde = '';
  hasta = '';
  id_sucursal = 0;
  sucursales: any[] = [];
  /** Mes del resumen mensual (YYYY-MM): al elegirlo se busca del 1 al último día. */
  mes = '';
  /** Reporte E; se pide aparte porque admite rangos de hasta un año. Rango con que se cargó: "desde/hasta/sucursal". */
  colegios: any[] = [];
  colegiosSinVisitas: any[] = [];
  rangoColegios = '';
  cargandoColegios = false;
  filtroColegio = '';
  verSinVisitas = false;
  /** Totales del equipo para la última fila del resumen mensual. */
  totalEquipo: any = null;
  cargando = false;

  asesores: any[] = [];
  /** Filas del resumen B: asesor y día, sólo días con turno o con algo marcado. */
  filas: any[] = [];
  /** Nombres de los turnos para las columnas del resumen, en orden horario. */
  nombresTurnos: string[] = ['Turno 1', 'Turno 2'];

  asesorDetalle: any = null;
  /** Fecha (YYYY-MM-DD) a la que se baja desde el resumen; vacío = todo el rango. */
  diaDetalle = '';

  /** Ajuste de día que se está cargando: {fecha (YYYY-MM-DD), turnos, id_turno, tipo, minutos, observacion}. */
  ajuste: any = null;
  guardandoAjuste = false;
  tiposAjuste = [
    { codigo: 'REUNION', nombre: 'Reunión', ayuda: 'Se reconoce el turno completo, sin pedir lugares.' },
    { codigo: 'PERMISO', nombre: 'Permiso', ayuda: 'Se reconocen los minutos del permiso; vacío = el turno completo.' },
    { codigo: 'FALTA', nombre: 'Falta', ayuda: 'El turno no se reconoce.' },
    { codigo: 'FERIADO', nombre: 'Feriado / no laborable', ayuda: 'Sólo para este asesor: 0 horas esperadas.' }
  ];

  constructor(public activeModal: NgbActiveModal,
    private empleadosService: EmpleadosService,
    private empresaService: EmpresaService,
    private modalService: NgbModal,
    private toastr: ToastrService) { }

  ngOnInit(): void {
    // Por defecto ayer: el resumen se revisa cada mañana sobre el día anterior.
    const ayer = new Date();
    ayer.setDate(ayer.getDate() - 1);
    this.desde = this.texto(ayer);
    this.hasta = this.texto(ayer);
    this.empresaService.obtenerSucursalesEmpresa(this.id_empresa).subscribe((s: any) => {
      this.sucursales = Array.isArray(s) ? s : [];
    });
    this.buscar();
  }

  buscar() {
    if (!this.desde || !this.hasta) return;
    if (this.pestana === 'colegios') {
      this.buscarColegios();
      return;
    }
    this.cargando = true;
    this.empleadosService.obtenerReporteControlCampo(this.id_empresa, this.id_sucursal || 0, 0, this.desde, this.hasta)
      .subscribe((res: any) => {
        this.cargando = false;
        if (res.tiene_error) {
          this.toastr.error(res.mensaje);
          return;
        }
        this.asesores = res.asesores || [];
        this.armarFilas();
        this.totalEquipo = this.sumarResumenes();
        const anterior = this.asesorDetalle && this.asesores.find(a => a.id_empleado === this.asesorDetalle.id_empleado);
        this.asesorDetalle = anterior || this.asesores[0] || null;
      }, () => {
        this.cargando = false;
        this.toastr.error('No se pudo obtener el reporte.');
      });
  }

  private armarFilas() {
    const nombres: string[] = [];
    this.filas = [];
    for (const asesor of this.asesores) {
      for (const dia of asesor.dias) {
        dia.turnos.forEach((t: any, i: number) => { if (!nombres[i]) nombres[i] = t.nombre; });
        if (this.hayAlgo(dia)) this.filas.push({ asesor, dia });
      }
    }
    this.nombresTurnos = [nombres[0] || 'Turno 1', nombres[1] || 'Turno 2'];
  }

  cambiarPestana(pestana: Pestana) {
    this.pestana = pestana;
    if (pestana === 'colegios' && this.rangoColegios !== this.claveRango()) this.buscarColegios();
  }

  private claveRango(): string {
    return this.desde + '/' + this.hasta + '/' + (this.id_sucursal || 0);
  }

  buscarColegios() {
    this.cargandoColegios = true;
    this.empleadosService.obtenerVisitasColegioControlCampo(this.id_empresa, this.id_sucursal || 0, this.desde, this.hasta)
      .subscribe((res: any) => {
        this.cargandoColegios = false;
        if (res.tiene_error) {
          this.toastr.error(res.mensaje);
          return;
        }
        this.colegios = res.lugares || [];
        this.colegiosSinVisitas = res.sin_visitas || [];
        this.rangoColegios = this.claveRango();
      }, () => {
        this.cargandoColegios = false;
        this.toastr.error('No se pudieron obtener las visitas por colegio.');
      });
  }

  colegiosFiltrados(): any[] {
    const texto = this.filtroColegio.trim().toUpperCase();
    if (!texto) return this.colegios;
    return this.colegios.filter(c => String(c.lugar).toUpperCase().includes(texto) ||
      c.asesores.some((a: any) => String(a.nombre).toUpperCase().includes(texto)));
  }

  sinVisitasFiltrados(): any[] {
    const texto = this.filtroColegio.trim().toUpperCase();
    return texto ? this.colegiosSinVisitas.filter(c => String(c.lugar).toUpperCase().includes(texto)) : this.colegiosSinVisitas;
  }

  textoAsesores(colegio: any): string {
    return colegio.asesores.map((a: any) => a.nombre + ' (' + a.visitas + ')').join(', ');
  }

  fechaHora(valor: any): string {
    return valor ? this.fecha(valor) + ' ' + this.hora(valor) : '';
  }

  verMapa(dia: any) {
    const modal = this.modalService.open(MapaRecorridoCampoComponent, { windowClass: 'mapa-recorrido-campo', size: 'xl' });
    modal.componentInstance.id_empresa = this.id_empresa;
    modal.componentInstance.asesor = this.asesorDetalle;
    modal.componentInstance.dia = dia;
  }

  elegirMes() {
    if (!this.mes) return;
    const [a, m] = this.mes.split('-').map(Number);
    this.desde = this.texto(new Date(a, m - 1, 1));
    this.hasta = this.texto(new Date(a, m, 0));
    this.buscar();
  }

  /** Columnas del resumen mensual: [título, campo del resumen, formato]. */
  columnasMensual: [string, string, 'n' | 'h' | '%'][] = [
    ['Días laborales', 'dias_laborales', 'n'], ['Cumple', 'dias_cumple', 'n'], ['Parcial', 'dias_parcial', 'n'],
    ['Sin trabajo', 'dias_sin_trabajo', 'n'], ['Falta', 'dias_falta', 'n'], ['Feriados', 'dias_feriado', 'n'],
    ['Horas esperadas', 'minutos_esperados', 'h'], ['Horas reconocidas', 'minutos_reconocidos', 'h'], ['%', 'cumplimiento', '%'],
    ['Lugares', 'lugares', 'n'], ['Días con meta', 'dias_meta', 'n'], ['Min. de hueco', 'minutos_huecos', 'n'],
    ['Atrasos', 'atrasos', 'n'], ['Salidas anticipadas', 'salidas_anticipadas', 'n'], ['Errores de hora', 'errores_hora', 'n'],
    ['Marcas lejos', 'marcas_lejos', 'n'], ['Horas de permiso', 'minutos_permiso', 'h']
  ];

  valorMensual(resumen: any, columna: [string, string, string]): string {
    const v = resumen?.[columna[1]];
    if (columna[2] === 'h') return this.horas(v);
    if (columna[2] === '%') return this.porcentaje(v);
    return String(v ?? 0);
  }

  private sumarResumenes(): any {
    if (!this.asesores.length) return null;
    const total: any = {};
    for (const a of this.asesores) {
      for (const [clave, valor] of Object.entries(a.resumen || {})) {
        if (typeof valor === 'number') total[clave] = (total[clave] || 0) + valor;
      }
    }
    total.cumplimiento = total.minutos_esperados ? Math.round(total.minutos_reconocidos / total.minutos_esperados * 1000) / 10 : null;
    return total;
  }

  verDetalle(fila: any) {
    this.asesorDetalle = fila.asesor;
    this.diaDetalle = this.texto(new Date(fila.dia.fecha));
    this.pestana = 'detalle';
  }

  diasDetalle(): any[] {
    if (!this.asesorDetalle) return [];
    return this.asesorDetalle.dias.filter((d: any) =>
      (!this.diaDetalle || this.texto(new Date(d.fecha)) === this.diaDetalle) && this.hayAlgo(d));
  }

  /** Días que se muestran: con turno, con algo marcado, feriado o con ajuste. */
  private hayAlgo(dia: any): boolean {
    return dia.turnos.length > 0 || dia.visitas_fuera_de_turno > 0 || !!dia.feriado || (dia.ajustes || []).length > 0;
  }

  // ---------- ajustes de día ----------

  fechaDia(dia: any): string {
    return this.texto(new Date(dia.fecha));
  }

  abrirAjuste(dia: any) {
    this.ajuste = {
      fecha: this.fechaDia(dia),
      turnos: dia.turnos,
      id_turno: null,
      tipo: 'PERMISO',
      minutos: null,
      observacion: ''
    };
  }

  ayudaAjuste(): string {
    return this.tiposAjuste.find(t => t.codigo === this.ajuste?.tipo)?.ayuda || '';
  }

  guardarAjuste() {
    const a = this.ajuste;
    if (!a.observacion?.trim()) {
      this.toastr.warning('Escribí el motivo del ajuste.');
      return;
    }
    this.guardandoAjuste = true;
    this.empleadosService.guardarAjusteControlCampo(this.id_empresa, {
      id_empleado: this.asesorDetalle.id_empleado,
      fecha: a.fecha,
      id_turno: a.id_turno,
      tipo: a.tipo,
      minutos: a.tipo === 'PERMISO' ? a.minutos : null,
      observacion: a.observacion
    }).subscribe((res: any) => {
      this.guardandoAjuste = false;
      if (res.tiene_error) {
        this.toastr.error(res.mensaje);
        return;
      }
      this.toastr.success(res.mensaje);
      this.ajuste = null;
      this.buscar();
    }, () => {
      this.guardandoAjuste = false;
      this.toastr.error('No se pudo guardar el ajuste.');
    });
  }

  quitarAjuste(ajuste: any) {
    if (!confirm('¿Quitar este ajuste? El día se vuelve a calcular con lo marcado.')) return;
    this.empleadosService.quitarAjusteControlCampo(this.id_empresa, ajuste.id).subscribe((res: any) => {
      if (res.tiene_error) {
        this.toastr.error(res.mensaje);
        return;
      }
      this.buscar();
    });
  }

  nombreTipoAjuste(codigo: string): string {
    return this.tiposAjuste.find(t => t.codigo === codigo)?.nombre || codigo;
  }

  /** "Permiso 90 min (MAÑANA): médico". */
  textoAjuste(dia: any, ajuste: any): string {
    const turno = ajuste.id_turno ? dia.turnos.find((t: any) => t.id_turno === ajuste.id_turno)?.nombre || 'turno' : 'todo el día';
    return this.nombreTipoAjuste(ajuste.tipo) + (ajuste.minutos ? ' ' + ajuste.minutos + ' min' : '') + ' (' + turno + ')' +
      (ajuste.observacion ? ': ' + ajuste.observacion : '');
  }

  /** Feriado y ajustes del día en una línea, para el resumen y las exportaciones. */
  textoAjustesDia(dia: any): string {
    const partes = (dia.ajustes || []).map((a: any) => this.textoAjuste(dia, a));
    if (dia.feriado) partes.unshift('Feriado: ' + dia.feriado);
    return partes.join('; ');
  }

  // ---------- formato ----------

  hora(valor: any): string {
    if (!valor) return '';
    const d = new Date(valor);
    return String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');
  }

  fecha(valor: any): string {
    const d = new Date(valor);
    const dias = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
    return dias[d.getDay()] + ' ' + String(d.getDate()).padStart(2, '0') + '/' + String(d.getMonth() + 1).padStart(2, '0');
  }

  horas(minutos: number): string {
    return (Math.round((minutos || 0) / 60 * 100) / 100).toFixed(2).replace('.', ',');
  }

  porcentaje(valor: number | null): string {
    return valor === null || valor === undefined ? '—' : String(valor).replace('.', ',') + ' %';
  }

  minutos(valor: number | null): string {
    return valor === null || valor === undefined ? '—' : valor + ' min';
  }

  turno(dia: any, i: number): any {
    return dia.turnos[i] || null;
  }

  lugaresDia(dia: any): string {
    return dia.turnos.map((t: any) => t.es_reunion ? 'R' : t.lugares).join(' / ') || '0';
  }

  claseResultado(resultado: string): string {
    return { 'Cumple': 'badge-success', 'Parcial': 'badge-warning', 'Sin trabajo': 'badge-danger', 'Falta': 'badge-danger',
      'En curso': 'badge-info', 'Feriado': 'badge-light' }[resultado] || 'badge-secondary';
  }

  urlFoto(foto: string): string {
    return this.rest_server + String(foto).replace(/^\.?\//, '');
  }

  // ---------- exportar ----------

  private filasResumen(): any[][] {
    return this.filas.map(f => {
      const m = this.turno(f.dia, 0), t = this.turno(f.dia, 1);
      return [f.asesor.nombre, this.fecha(f.dia.fecha),
        this.hora(m?.ingreso), this.hora(m?.salida), this.hora(t?.ingreso), this.hora(t?.salida),
        this.lugaresDia(f.dia), this.horas(f.dia.minutos_esperados), this.horas(f.dia.minutos_reconocidos),
        this.porcentaje(f.dia.cumplimiento), f.dia.resultado, this.textoAjustesDia(f.dia), f.dia.alertas.join('; ')];
    });
  }

  private encabezadoResumen(): string[] {
    const [uno, dos] = this.nombresTurnos;
    return ['Asesor', 'Fecha', 'Ingreso ' + uno, 'Salida ' + uno, 'Ingreso ' + dos, 'Salida ' + dos,
      'Lugares', 'Horas esperadas', 'Horas reconocidas', '%', 'Resultado', 'Ajustes', 'Alertas'];
  }

  private filasDetalle(asesor: any): any[][] {
    const filas: any[][] = [];
    for (const dia of asesor.dias) {
      for (const t of dia.turnos) {
        for (const v of t.visitas) {
          filas.push([asesor.nombre, this.fecha(dia.fecha), t.nombre, v.lugar, this.hora(v.llegada), this.hora(v.salida),
            v.minutos_lugar ?? '', v.minutos_traslado ?? '', v.minutos_hueco || '', v.distancia ?? '',
            v.error || (v.lejos ? 'Lejos del colegio' : ''), v.observacion || '']);
        }
        if (t.visitas.length || t.minutos_esperados) {
          filas.push([asesor.nombre, this.fecha(dia.fecha), t.nombre, 'RESULTADO DEL TURNO',
            this.hora(t.ingreso_cuenta), this.hora(t.salida_cuenta), '', '', t.minutos_huecos || '', '',
            this.horas(t.minutos_reconocidos) + ' h de ' + this.horas(t.minutos_esperados) + ' h · ' + t.lugares + ' lugares', (t.ajuste ? 'Ajuste: ' + this.textoAjuste(dia, t.ajuste) + '; ' : '') + t.alertas.concat(t.errores).join('; ')]);
        }
      }
    }
    return filas;
  }

  private encabezadoDetalle(): string[] {
    return ['Asesor', 'Fecha', 'Turno', 'Lugar', 'Llegada', 'Salida', 'Tiempo (min)', 'Traslado (min)', 'Hueco (min)', 'Distancia (m)', 'Observación del sistema', 'Observación'];
  }

  private encabezadoColegios(): string[] {
    return ['Colegio', 'Dirección', 'Visitas', 'Días', 'Asesores (visitas)', 'Última visita', 'Último asesor', 'Tiempo promedio (min)', 'Marcas lejos'];
  }

  /** Los visitados y, al final, los que no tuvieron visitas. */
  private filasColegios(): any[][] {
    return this.colegiosFiltrados().map(c => [c.lugar, c.direccion || '', c.visitas, c.dias, this.textoAsesores(c),
      this.fechaHora(c.ultima), c.ultimo_asesor, c.minutos_promedio ?? '', c.lejos || ''])
      .concat(this.sinVisitasFiltrados().map(c => [c.lugar, c.direccion || '', 0, 0, 'SIN VISITAS', '', '', '', '']));
  }

  private encabezadoMensual(): string[] {
    return ['Asesor', ...this.columnasMensual.map(c => c[0])];
  }

  private filasMensual(): any[][] {
    const filas = this.asesores.map(a => [a.nombre, ...this.columnasMensual.map(c => this.valorMensual(a.resumen, c))]);
    if (this.totalEquipo) filas.push(['TOTAL EQUIPO', ...this.columnasMensual.map(c => this.valorMensual(this.totalEquipo, c))]);
    return filas;
  }

  async exportarExcel() {
    const libro = new Workbook();
    const agregar = (nombre: string, encabezado: string[], filas: any[][]) => {
      const hoja = libro.addWorksheet(nombre);
      const cabecera = hoja.addRow(encabezado);
      cabecera.eachCell(c => {
        c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '1F3864' } };
        c.font = { bold: true, color: { argb: 'FFFFFF' } };
      });
      filas.forEach(f => hoja.addRow(f));
      hoja.columns.forEach(col => { col.width = 16; });
    };
    agregar('Resumen equipo', this.encabezadoResumen(), this.filasResumen());
    agregar('Detalle visitas', this.encabezadoDetalle(), this.asesores.flatMap(a => this.filasDetalle(a)));
    agregar('Resumen mensual', this.encabezadoMensual(), this.filasMensual());
    if (this.rangoColegios === this.claveRango()) agregar('Visitas por colegio', this.encabezadoColegios(), this.filasColegios());
    const datos = await libro.xlsx.writeBuffer();
    fs.saveAs(new Blob([datos], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
      'Control campo ' + this.desde + ' a ' + this.hasta + '.xlsx');
  }

  exportarPdf() {
    const doc = new jsPDF('l', 'pt', 'letter');
    const titulo = (texto: string) => {
      doc.setFontSize(13);
      doc.text(texto, 40, 36);
      doc.setFontSize(9);
      doc.text((this.razon_social ? this.razon_social + ' · ' : '') + 'Del ' + this.desde + ' al ' + this.hasta, 40, 52);
    };
    if (this.pestana === 'colegios') {
      titulo('Visitas por colegio');
      autoTable(doc, { startY: 62, head: [this.encabezadoColegios()], body: this.filasColegios(),
        styles: { fontSize: 7 }, headStyles: { fillColor: [31, 56, 100] } });
    } else if (this.pestana === 'mensual') {
      titulo('Resumen del período por asesor');
      autoTable(doc, { startY: 62, head: [this.encabezadoMensual()], body: this.filasMensual(),
        styles: { fontSize: 7 }, headStyles: { fillColor: [31, 56, 100] },
        didParseCell: (d: any) => { if (d.section === 'body' && d.row.raw[0] === 'TOTAL EQUIPO') d.cell.styles.fontStyle = 'bold'; } });
    } else if (this.pestana === 'equipo') {
      titulo('Resumen diario del equipo');
      autoTable(doc, { startY: 62, head: [this.encabezadoResumen()], body: this.filasResumen(),
        styles: { fontSize: 7 }, headStyles: { fillColor: [31, 56, 100] } });
    } else if (this.asesorDetalle) {
      titulo('Detalle diario de visitas · ' + this.asesorDetalle.nombre);
      autoTable(doc, { startY: 62, head: [this.encabezadoDetalle()], body: this.filasDetalle(this.asesorDetalle),
        styles: { fontSize: 7 }, headStyles: { fillColor: [31, 56, 100] },
        didParseCell: (d: any) => { if (d.section === 'body' && d.row.raw[3] === 'RESULTADO DEL TURNO') d.cell.styles.fontStyle = 'bold'; } });
    }
    const nombre = { equipo: 'Resumen equipo ', mensual: 'Resumen mensual ', colegios: 'Visitas por colegio ', detalle: 'Detalle ' + (this.asesorDetalle?.nombre || '') + ' ' }[this.pestana];
    doc.save(nombre + this.desde + '.pdf');
  }

  private texto(d: Date): string {
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  }
}
